import { FunctionComponent, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Divider,
  Flex,
  Link,
} from '@chakra-ui/react';

import {
  CompanyLegalBeneficiary,
  CompanyLegalBeneficiaryOnboardingStatus,
} from '../../../../../../__generated__/GQL';
import useCompanyContext from '../../../../../../common/hooks/useCompanyContext';
import { useCompanyProfile } from '../../../../../../common/hooks/useCompanyProfile';
import Callout from '../../../../../../components/_core/Callout';
import Heading from '../../../../../../components/_core/Heading';
import SunshineIcon from '../../../../../../components/_core/SunshineIcon';
import Text from '../../../../../../components/_core/Text';
import { getCurrentBeneficiariesEmails } from '../../../../components/BeneficiaryForm/helpers';
import { beneficiariesDocsStatusQuery } from '../../../../legacy/Pages/BeneficiariesDocs/graphql';
import { User } from '../../../../legacy/types';
import { throwIfErrorPresent } from '../../../../utils';
import DownloadAppSection from '../DownloadAppSection';
import BeneficiaryCard from './components/BeneficiaryCard';
import BeneficiarySection from './components/BeneficiarySection';
import CompanyCard from './components/CompanyCard';
import { CompanyCardStatuses } from './components/CompanyCard/CompanyCard';
import CreateBeneficiary from './components/CreateBeneficiary';
import SkeletonCard from './components/SkeletonCard';
import locales from './locales';

export interface BeneficiariesDocsStatusQueryData {
  companyLegalBeneficiaries: CompanyLegalBeneficiary[];
}

export interface BeneficiariesDocsStatusQueryVariables {
  companyProfileId: string;
}

const MultiKycContent: FunctionComponent<{ user: User }> = ({ user }) => {
  const { companyProfileId } = useCompanyContext();
  const [isAddingNewBeneficiary, setIsAddingNewBeneficiary] = useState(false);

  const { data, error } = useQuery<
    BeneficiariesDocsStatusQueryData,
    BeneficiariesDocsStatusQueryVariables
  >(beneficiariesDocsStatusQuery, {
    skip: !companyProfileId,
    variables: {
      companyProfileId: companyProfileId ?? '',
    },
  });
  throwIfErrorPresent(error);

  const { company } = useCompanyProfile(companyProfileId);

  const beneficiaryMap = {
    completed: [],
    error: [],
    inProgress: [],
    notStarted: [],
  };

  const beneficiaries = data
    ? data?.companyLegalBeneficiaries.reduce<{
        notStarted: CompanyLegalBeneficiary[];
        inProgress: CompanyLegalBeneficiary[];
        completed: CompanyLegalBeneficiary[];
        error: CompanyLegalBeneficiary[];
      }>((acc, beneficiary) => {
        if (beneficiary.isCurrentUser) {
          return acc;
        }

        switch (beneficiary.onboardingStatus) {
          case CompanyLegalBeneficiaryOnboardingStatus.NotStarted: {
            acc.notStarted.push(beneficiary);
            break;
          }

          case CompanyLegalBeneficiaryOnboardingStatus.InProgress: {
            acc.inProgress.push(beneficiary);
            break;
          }

          case CompanyLegalBeneficiaryOnboardingStatus.InReview:
          case CompanyLegalBeneficiaryOnboardingStatus.Validated: {
            acc.completed.push(beneficiary);
            break;
          }

          case CompanyLegalBeneficiaryOnboardingStatus.Refused:
          case CompanyLegalBeneficiaryOnboardingStatus.Invalid:
          default: {
            acc.error.push(beneficiary);
            break;
          }
        }

        return acc;
      }, beneficiaryMap)
    : beneficiaryMap;

  const totalBeneficiaries = Object.values(beneficiaries).flat().length;

  const companyCardStatus = (() => {
    if (beneficiaryMap.error.length) {
      return CompanyCardStatuses.Error;
    }

    if (beneficiaryMap.completed.length === totalBeneficiaries) {
      return CompanyCardStatuses.Verifying;
    }

    return CompanyCardStatuses.Pending;
  })();

  const currentBeneficiariesEmails = getCurrentBeneficiariesEmails({
    accountHolderEmail: user.email,
    beneficiaries: data?.companyLegalBeneficiaries ?? [],
  });

  return isAddingNewBeneficiary ? (
    <CreateBeneficiary
      currentBeneficiariesEmails={currentBeneficiariesEmails}
      onDone={() => setIsAddingNewBeneficiary(false)}
    />
  ) : (
    <Flex flexDirection="column">
      <Heading marginBottom="space-24" size="lg">
        {locales.heading}
      </Heading>
      <Box marginBottom="space-40">
        {data && company ? (
          <CompanyCard name={company.legalName} status={companyCardStatus} />
        ) : (
          <SkeletonCard />
        )}
      </Box>
      <Divider />
      <Text
        fontWeight="weight-500"
        marginY="space-32"
        size="lg"
        variant="primary"
      >
        {locales.beneficiarySectionHeading}
      </Text>

      {data ? (
        <>
          <BeneficiarySection
            beneficiaries={beneficiaries.error}
            currentBeneficiariesEmails={currentBeneficiariesEmails}
            description={locales.invalidSectionDescription}
            icon="warning"
            title={locales.invalidSectionTitle}
          />
          <BeneficiarySection
            beneficiaries={beneficiaries.notStarted}
            currentBeneficiariesEmails={currentBeneficiariesEmails}
            icon="clock"
            title={locales.notStartedSectionTitle}
          />
          <BeneficiarySection
            beneficiaries={beneficiaries.inProgress}
            currentBeneficiariesEmails={currentBeneficiariesEmails}
            icon="edit-file"
            title={locales.inProgressSectionTitle}
          />
          <Accordion allowToggle>
            <AccordionItem border={0} marginBottom="space-16">
              <AccordionButton padding={0}>
                <Box alignItems="center" display="flex" flex={1}>
                  <SunshineIcon
                    color="grey.700"
                    marginRight="space-8"
                    name="tick"
                    size="icon-16"
                  />
                  <Text>{locales.informationCompleteSectionTitle}</Text>
                </Box>
                <Box alignItems="center" display="flex">
                  <Text>
                    {locales.formatString(locales.completedBeneficiaryCount, {
                      completed: beneficiaries.completed.length,
                      total: totalBeneficiaries,
                    })}
                  </Text>
                  {beneficiaries.completed.length ? (
                    <AccordionIcon
                      color="grey.600"
                      height="24px"
                      marginLeft="space-8"
                      width="24px"
                    />
                  ) : null}
                </Box>
              </AccordionButton>
              {beneficiaries.completed.length ? (
                <AccordionPanel marginTop="space-16" padding={0}>
                  {beneficiaries.completed.map((beneficiary) => (
                    <Box
                      key={beneficiary.companyLegalBeneficiaryId}
                      marginBottom="space-8"
                      width="100%"
                    >
                      <BeneficiaryCard
                        beneficiary={beneficiary}
                        currentBeneficiariesEmails={currentBeneficiariesEmails}
                      />
                    </Box>
                  ))}
                </AccordionPanel>
              ) : null}
            </AccordionItem>
          </Accordion>
          {beneficiaryMap.completed.length === totalBeneficiaries ? (
            <>
              <Divider marginY="space-40" />
              <DownloadAppSection />
            </>
          ) : (
            <Callout hideIcon marginTop="space-40">
              <Text>
                {locales.addBeneficiaryAdornment}{' '}
                <Link
                  onClick={() => {
                    setIsAddingNewBeneficiary(true);
                  }}
                >
                  {locales.addBeneficiary}
                </Link>
              </Text>
            </Callout>
          )}
        </>
      ) : (
        <>
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </>
      )}
    </Flex>
  );
};

export default MultiKycContent;
